import { call, put, select, takeLatest } from 'redux-saga/effects';

import { getPromozavrTestIdsFromExperimentBoxes } from '../../../../../common/helpers';
import type { RootState } from '../../../../init-store';
import { createLogger } from '../../helpers/loggerFacade';
import { selectIsYandexNet } from '../config/selectors';
import { selectPromo } from './api';
import { getPromozavrRendererSource } from './renderer-source';
import { resolvePromozavrInit } from './resolve-promozavr-init';
import { selectShouldUsePaas, selectShouldUsePdokPromoRenderer } from './selectors';
import { failPromozavrRequest, finishPromozavrRequest, initPromozavr, startPromozavrRequest } from './slice';
import type { SelectPromoModelResponse } from './types';

const logger = createLogger('promozavr');

export function* initPromozavrSaga(action: ReturnType<typeof initPromozavr>) {
    const { payload } = action;
    const state: RootState = yield select();
    const resolvedInit = resolvePromozavrInit(
        {
            ...payload,
            lang: state.config.appLang,
            tld: state.config.tld,
            testIds: getPromozavrTestIdsFromExperimentBoxes(state.config.experiments?.boxes),
            isYandexNet: selectIsYandexNet(state),
            allowDebugParams: state.config.environment === 'development',
            shouldUsePaas: selectShouldUsePaas(state),
            shouldUsePdokPromoRenderer: selectShouldUsePdokPromoRenderer(state),
        },
        getPromozavrRendererSource
    );

    if (!resolvedInit) {
        return;
    }
    const { request, requestKey, rendererSource } = resolvedInit;

    yield put(
        startPromozavrRequest({
            request,
            requestKey,
            rendererSource,
        })
    );

    try {
        const response: SelectPromoModelResponse = yield call(selectPromo, request);

        logger.info('selectPromo resolved', {
            request,
            response,
            rendererSource,
        });

        yield put(finishPromozavrRequest(response));
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);

        logger.error(error, {
            method: 'initPromozavrSaga',
            additional: {
                request,
                requestKey,
                rendererSource,
            },
        });

        yield put(failPromozavrRequest(errorMessage));
    }
}

export default function* promozavrSaga(): Generator {
    yield takeLatest(initPromozavr.type, initPromozavrSaga);
}
